import React from "react";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Select from "@cloudscape-design/components/select";
import Checkbox from "@cloudscape-design/components/checkbox";
import Container from "@cloudscape-design/components/container";
import Table from "@cloudscape-design/components/table";
import Grid from "@cloudscape-design/components/grid";
import { formatChestPrizeType } from "../helpers/chestPrizeTypes";

const getChestRewardsPayload = (rewardConditions) => {
	const parsed = parseRewardConditions(rewardConditions, []);

	if (Array.isArray(parsed)) {
		return parsed;
	}

	if (Array.isArray(parsed?.prizes)) {
		return parsed.prizes;
	}

	return [];
};

const normalizeChestPrizeValue = (value) => {
	if (Array.isArray(value)) {
		return value.map((entry) => normalizeChestPrizeValue(entry));
	}

	if (value && typeof value === "object") {
		return Object.keys(value)
			.sort()
			.reduce((accumulator, key) => {
				accumulator[key] = normalizeChestPrizeValue(value[key]);
				return accumulator;
			}, {});
	}

	return value;
};

const getChestPrizeSignature = ({ type, rewardConditions }) =>
	JSON.stringify({
		type: type || "",
		rewardConditions: normalizeChestPrizeValue(getChestRewardsPayload(rewardConditions)),
	});

const findMatchingChestPrize = (items = [], { type, rewardConditions }) => {
	const chestPrizeId = getChestPrizeIdFromRewardConditions(rewardConditions);
	if (chestPrizeId) {
		return (items || []).find((item) => Number(item.id) === Number(chestPrizeId)) || null;
	}

	const targetSignature = getChestPrizeSignature({ type, rewardConditions });

	return (
		(items || []).find(
			(item) =>
				getChestPrizeSignature({
					type: item.type,
					rewardConditions: item.rewardConditions,
				}) === targetSignature
		) || null
	);
};

const getChestPrizeIdFromRewardConditions = (rewardConditions) => {
	const parsed = parseRewardConditions(rewardConditions);

	if (Array.isArray(parsed)) {
		return null;
	}

	return parsed?.chestPrizeId || null;
};

const formatChestRewardsSummary = (rewardConditions, fallback = "Chest config") => {
	const rewards = getChestRewardsPayload(rewardConditions);
	if (!rewards.length) {
		return fallback;
	}

	const totalProbability = rewards.reduce(
		(sum, reward) => sum + (Number(reward.probability) || 0),
		0
	);

	return `Chest • ${rewards.length} rewards • ${totalProbability}%`;
};

const getChestPrizeDisplayLabel = (items = [], { type, rewardConditions, fallback = "Chest config" }) => {
	const match = findMatchingChestPrize(items, { type, rewardConditions });

	return match ? `${match.name} (${match.id})` : formatChestRewardsSummary(rewardConditions, fallback);
};

export const INITIAL_DAILY_PROGRESSIVE_CONFIG = {
	breakOnMissedDay: false,
	resetType: "full",
	penaltyDays: 0,
	dailyConditions: {
		type: "deposit",
		deposit: { amount: 0, paymentType: "both" },
		spins: { count: 0, minBet: 0, gameId: null, providerId: null },
	},
	milestones: [],
};

export const INITIAL_NEW_MILESTONE = {
	day: "",
	rewardType: null,
	rewardValue: "",
	freespinsProvider: null,
	freespinsGame: null,
	freespinsBetAmount: "",
	freespinsExpiresAt: "",
	chestRewards: [],
	chestType: "",
	chestPrizeId: null,
	chestPrizeSelected: null,
};

export const CONDITION_TYPE_OPTIONS = [
	{ value: "deposit", label: "Deposit" },
	{ value: "spins", label: "Spins" },
	{ value: "combined", label: "Deposit AND Spins" },
];

export const PAYMENT_TYPE_OPTIONS = [
	{ value: "crypto", label: "Crypto" },
	{ value: "fiat", label: "Fiat" },
	{ value: "both", label: "Both" },
];

export const RESET_TYPE_OPTIONS = [
	{ value: "full", label: "Full Reset" },
	{ value: "penalty", label: "Penalty" },
];

export const REWARD_TYPE_OPTIONS = [
	{ value: "money", label: "Money" },
	{ value: "freespins", label: "Freespins" },
	{ value: "tickets", label: "Tickets" },
	{ value: "chest", label: "Chest" },
];

export const FREESPINS_PROVIDER_OPTIONS = [
	{ value: "endorphina", label: "Endorphina", type: 10 },
];

const parseRewardConditions = (rewardConditions) => {
	if (!rewardConditions) {
		return {};
	}

	if (typeof rewardConditions === "string") {
		try {
			return JSON.parse(rewardConditions);
		} catch (e) {
			return {};
		}
	}

	return rewardConditions;
};

export const formatGamesForSelect = (games = []) => {
	return games.map((game) => ({
		label: game.description || game.name,
		value: game.description || game.name,
		gameId: game.id || game.gameID,
		game: game,
	}));
};

export const getGamesByProvider = (allGames = [], providerId) => {
	if (!providerId) return [];

	const filteredGames = allGames.filter(
		(game) => game.providerID === providerId && game.active === 1
	);

	return filteredGames.map((game) => ({
		id: game.gameID,
		description: game.name,
		name: game.name,
		gameID: game.gameID,
		providerID: game.providerID,
	}));
};

export const capitalizeFirst = (str) => {
	if (!str) return "";
	return str.charAt(0).toUpperCase() + str.slice(1);
};

class DailyProgressiveTab extends React.Component {
	renderDepositSettings() {
		const { config, onDepositConditionsChange } = this.props;
		const deposit = config.dailyConditions.deposit;

		return (
			<Container header={<Box variant="h4">Deposit Settings</Box>}>
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Minimum Amount" description="Minimum deposit amount">
						<Input
							type="number"
							placeholder="Enter amount"
							value={deposit.amount || ""}
							onChange={(e) => onDepositConditionsChange({ amount: parseInt(e.detail.value, 10) || 0 })}
						/>
					</FormField>
					<FormField label="Payment Type" description="Accepted payment types">
						<Select
							selectedOption={
								deposit.paymentType
									? { value: deposit.paymentType, label: capitalizeFirst(deposit.paymentType) }
									: null
							}
							onChange={({ detail }) => onDepositConditionsChange({ paymentType: detail.selectedOption.value })}
							options={PAYMENT_TYPE_OPTIONS}
							placeholder="Select payment type"
						/>
					</FormField>
				</Grid>
			</Container>
		);
	}

	renderSpinsSettings() {
		const { config, providerOptions, spinsGamesOptions, onSpinsConditionsChange, onLoadSpinsGames } = this.props;
		const spins = config.dailyConditions.spins;

		return (
			<Container header={<Box variant="h4">Spins Settings</Box>}>
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Spins Count" description="Minimum number of spins">
						<Input
							type="number"
							placeholder="Enter spins count"
							value={spins.count || ""}
							onChange={(e) => onSpinsConditionsChange({ count: parseInt(e.detail.value, 10) || 0 })}
						/>
					</FormField>
					<FormField label="Minimum Bet" description="Minimum bet amount">
						<Input
							type="number"
							placeholder="Enter minimum bet"
							value={spins.minBet || ""}
							onChange={(e) => onSpinsConditionsChange({ minBet: parseInt(e.detail.value, 10) || 0 })}
						/>
					</FormField>
					<FormField label="Provider (optional)" description="Specific provider">
						<Select
							filteringType="auto"
							placeholder="Any provider"
							selectedOption={
								spins.providerId
									? providerOptions.find((p) => p.type === spins.providerId) || null
									: null
							}
							onChange={({ detail }) => {
								onSpinsConditionsChange({ providerId: detail.selectedOption?.type || null, gameId: null });
								if (detail.selectedOption?.type) {
									onLoadSpinsGames(detail.selectedOption.type);
								}
							}}
							options={[{ label: "Any", value: null }, ...providerOptions]}
						/>
					</FormField>
					<FormField label="Game (optional)" description="Specific game">
						<Select
							filteringType="auto"
							placeholder="Any game"
							disabled={!spins.providerId}
							selectedOption={
								spins.gameId
									? spinsGamesOptions.find((g) => g.gameId === spins.gameId) || null
									: null
							}
							onChange={({ detail }) => onSpinsConditionsChange({ gameId: detail.selectedOption?.gameId || null })}
							options={[{ label: "Any", value: null }, ...spinsGamesOptions]}
						/>
					</FormField>
				</Grid>
			</Container>
		);
	}

	renderDailyConditions() {
		const { config, onDailyConditionsChange } = this.props;
		const conditionType = config.dailyConditions.type;

		const selectedConditionOption = conditionType
			? CONDITION_TYPE_OPTIONS.find((opt) => opt.value === conditionType) || { value: conditionType, label: capitalizeFirst(conditionType) }
			: null;

		return (
			<Container header={<Box variant="h3">Daily Conditions</Box>}>
				<SpaceBetween direction="vertical" size="m">
					<FormField label="Condition Type" description="Type of daily condition to complete">
						<Select
							selectedOption={selectedConditionOption}
							onChange={({ detail }) => onDailyConditionsChange({ type: detail.selectedOption.value })}
							options={CONDITION_TYPE_OPTIONS}
							placeholder="Select condition type"
						/>
					</FormField>

					{(conditionType === "deposit" || conditionType === "combined") && this.renderDepositSettings()}
					{(conditionType === "spins" || conditionType === "combined") && this.renderSpinsSettings()}
				</SpaceBetween>
			</Container>
		);
	}

	renderStreakSettings() {
		const { config, onConfigChange } = this.props;
		const showPenaltyDays = config.breakOnMissedDay && config.resetType === "penalty";

		return (
			<Container header={<Box variant="h3">Streak Settings</Box>}>
				<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
					<FormField label="Break on Missed Day" description="Reset streak when user misses a day">
						<Checkbox
							checked={config.breakOnMissedDay}
							onChange={({ detail }) => onConfigChange({ breakOnMissedDay: detail.checked })}
						>
							Break streak on missed day
						</Checkbox>
					</FormField>
					<FormField label="Reset Type" description="How to handle missed days">
						<Select
							disabled={!config.breakOnMissedDay}
							selectedOption={
								config.resetType
									? { value: config.resetType, label: config.resetType === "full" ? "Full Reset" : "Penalty" }
									: null
							}
							onChange={({ detail }) => onConfigChange({ resetType: detail.selectedOption.value })}
							options={RESET_TYPE_OPTIONS}
							placeholder="Select reset type"
						/>
					</FormField>
					<FormField label="Penalty Days" description="Days to subtract on miss">
						<Input
							disabled={!showPenaltyDays}
							type="number"
							placeholder="Enter penalty days"
							value={config.penaltyDays || ""}
							onChange={(e) => onConfigChange({ penaltyDays: parseInt(e.detail.value, 10) || 0 })}
						/>
					</FormField>
				</Grid>
			</Container>
		);
	}

	renderMilestoneForm() {
		const {
			newMilestone,
			milestoneGamesOptions,
			onNewMilestoneChange,
			onLoadMilestoneGames,
			onAddMilestone,
			chestPrizeOptions,
			chestPrizeLoading,
			onSelectChestPrize,
			onLoadChestPrizeOptions,
		} = this.props;

		return (
			<FormField stretch label="Add Milestone">
				<SpaceBetween direction="vertical" size="m">
					<Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 4 }, { colspan: 2 }]}>
						<FormField label="Day">
							<Input
								type="number"
								placeholder="Day"
								value={newMilestone.day}
								onChange={(e) => onNewMilestoneChange({ day: e.detail.value })}
							/>
						</FormField>
						<FormField label="Reward Type">
							<Select
								selectedOption={newMilestone.rewardType}
								onChange={({ detail }) => {
									onNewMilestoneChange({
										rewardType: detail.selectedOption,
										rewardValue: "",
										freespinsProvider: null,
										freespinsGame: null,
										freespinsBetAmount: "",
										freespinsExpiresAt: "",
										chestRewards: [],
										chestType: "",
										chestPrizeId: null,
										chestPrizeSelected: null,
									});

									if (detail.selectedOption.value === "chest") {
										onLoadChestPrizeOptions();
									}
								}}
								options={REWARD_TYPE_OPTIONS}
								placeholder="Type"
							/>
						</FormField>
						{newMilestone.rewardType?.value !== "chest" && (
							<FormField label="Value">
								<Input
									type="number"
									placeholder="Value"
									value={newMilestone.rewardValue}
									onChange={(e) => onNewMilestoneChange({ rewardValue: e.detail.value })}
								/>
							</FormField>
						)}
						{newMilestone.rewardType?.value === "freespins" && (
							<SpaceBetween direction="vertical" size="xs">
								<FormField label="Provider">
									<Select
										placeholder="Provider"
										selectedOption={newMilestone.freespinsProvider}
										onChange={({ detail }) => {
											onNewMilestoneChange({ freespinsProvider: detail.selectedOption, freespinsGame: null });
											onLoadMilestoneGames(detail.selectedOption.type);
										}}
										options={FREESPINS_PROVIDER_OPTIONS}
									/>
								</FormField>
								<FormField label="Game">
									<Select
										disabled={!newMilestone.freespinsProvider}
										placeholder="Game"
										selectedOption={newMilestone.freespinsGame}
										options={milestoneGamesOptions}
										onChange={({ detail }) => onNewMilestoneChange({ freespinsGame: detail.selectedOption })}
									/>
								</FormField>
								<FormField label="Bet Amount" description="e.g., 100 for 100.00 RUB">
									<Input
										type="number"
										placeholder="Bet"
										value={newMilestone.freespinsBetAmount}
										onChange={(e) => onNewMilestoneChange({ freespinsBetAmount: e.detail.value })}
									/>
								</FormField>
								<FormField label="Expires (days)">
									<Input
										type="number"
										placeholder="Days"
										value={newMilestone.freespinsExpiresAt}
										onChange={(e) => onNewMilestoneChange({ freespinsExpiresAt: e.detail.value })}
									/>
								</FormField>
							</SpaceBetween>
						)}
						<FormField label="Add">
							<Button onClick={onAddMilestone}>+ Add</Button>
						</FormField>
					</Grid>

					{newMilestone.rewardType?.value === "chest" && (
						<Container header={<Box variant="h4">Chest preset</Box>}>
							<SpaceBetween direction="vertical" size="m">
								<FormField label="Chest">
									<Select
										filteringType="auto"
										placeholder="Select chest preset"
										selectedOption={newMilestone.chestPrizeSelected}
										onChange={({ detail }) => onSelectChestPrize(detail.selectedOption)}
										options={chestPrizeOptions}
										statusType={chestPrizeLoading ? "loading" : "finished"}
										loadingText="Loading chest presets"
										empty="No chest presets found"
									/>
								</FormField>
								<Button variant="normal" onClick={onLoadChestPrizeOptions}>
									Reload chests
								</Button>
								{newMilestone.chestType && (
									<Box color="text-status-info">{`Chest type: ${formatChestPrizeType(newMilestone.chestType)}`}</Box>
								)}
								{(newMilestone.chestRewards || []).length > 0 && (
									<Box color="text-status-info">{`Rewards in chest: ${(newMilestone.chestRewards || []).length}`}</Box>
								)}
							</SpaceBetween>
						</Container>
					)}
				</SpaceBetween>
			</FormField>
		);
	}

	renderMilestones() {
		const { config, onRemoveMilestone, chestPrizeOptions } = this.props;
		const chestPrizeItems = (chestPrizeOptions || []).map((option) => option.chestPrize).filter(Boolean);

		const columnDefinitions = [
			{ id: "day", header: "Day", cell: (item) => item.day },
			{ id: "rewardType", header: "Reward Type", cell: (item) => capitalizeFirst(item.rewardType) },
			{
				id: "rewardValue",
				header: "Reward Value",
				cell: (item) => {
					if (item.rewardType !== "chest") {
						return item.rewardValue;
					}

					const rewardConditions = parseRewardConditions(item.rewardConditions);
					const chestType = Array.isArray(rewardConditions) ? "" : rewardConditions?.type || "";

					return getChestPrizeDisplayLabel(chestPrizeItems, {
						type: chestType,
						rewardConditions,
						fallback: "Chest config",
					});
				},
			},
			{
				id: "rewardConditions",
				header: "Conditions",
				cell: (item) => {
					if (item.rewardType === "freespins" && item.rewardConditions) {
						return `Game: ${item.rewardConditions.gameId}, Bet: ${item.rewardConditions.betAmount}, Expires: ${item.rewardConditions.expiresAt}d`;
					}
					if (item.rewardType === "chest") {
						const rewardConditions = parseRewardConditions(item.rewardConditions);
						const prizes = rewardConditions?.prizes || [];
						return prizes.length ? `${prizes.length} chest rewards` : "Chest config";
					}
					return "-";
				},
			},
			{
				id: "actions",
				header: "Control",
				cell: (item) => (
					<Button variant="icon" iconName="delete-marker" onClick={() => onRemoveMilestone(item.id)}>
						Remove
					</Button>
				),
			},
		];

		return (
			<Container header={<Box variant="h3">Milestones</Box>}>
				<Table
					columnDefinitions={columnDefinitions}
					items={config.milestones || []}
					footer={this.renderMilestoneForm()}
					empty="No milestones yet"
				/>
			</Container>
		);
	}

	render() {
		return (
			<SpaceBetween direction="vertical" size="l">
				{this.renderDailyConditions()}
				{this.renderStreakSettings()}
				{this.renderMilestones()}
			</SpaceBetween>
		);
	}
}

export default DailyProgressiveTab;
